import React, { useCallback, useEffect, useMemo, useState } from "react";

/**
 * THE DAY AT A GLANCE (2026-10-23).
 *
 * Asked for by the user, in their words: *"a calendar like display of the appointments for today … time 1 hour
 * increment on the Y axis and professional on the x axis. The time range is shop's opening & closing for the
 * specific day."*
 *
 * WHAT IT IS NOT. It is a GRID, not a calendar engine: an hour is a column, a professional is a row, and a
 * booking is drawn where it actually sits in the day. A block carries three short facts — start time, the
 * customer's first name, the service (user, 2026-10-23) — because the end time and a full range do not fit a
 * block meant to be read at a glance; the tooltip holds that detail.
 *
 * WHAT IT REFUSES TO GUESS. A closed day says so instead of drawing rows from midnight. A location with no
 * professionals says so instead of showing an empty grid that looks like a broken calendar. A failed request
 * shows the failure, not an empty day that reads as "nothing booked".
 *
 * HOURS COME FROM THE SHOP, not from constants: `businessHours[weekday]` decides the first and last row, so a
 * shop that opens at 10 and closes at 18 gets 10..18 and never a row it never opens.
 */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * THE PROFESSIONAL COLUMN, WIDENED (user, 2026-10-23: *"increase the professional axis space"*). At 82px every
 * name except the shortest was ellipsised to "Chr…"; the hours share whatever width is left, and a thinner hour
 * column is a cheaper loss than an unidentifiable row.
 */
const PROFESSIONAL_COLUMN_WIDTH = "132px";

/** "Dana Whitfield" -> "Dana": a block has one line to say who is in the chair. */
const firstNameOf = (value) => String(value || "").trim().split(/\s+/)[0] || "";

const pad = (value) => String(value).padStart(2, "0");

const todayInLocalTime = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const shiftDate = (dateString, days) => {
  const date = new Date(`${dateString}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** "14:30" -> "2:30 PM"; the label the grid shows on a booking block. */
const label12h = (hhmm) => {
  if (!/^\d{2}:\d{2}$/.test(String(hhmm || ""))) return hhmm || "";
  const [hour, minute] = hhmm.split(":").map(Number);
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${minute ? `:${pad(minute)}` : ""} ${suffix}`;
};

/** The hour rows from open to close, inclusive of the closing hour as the last boundary. */
const hourRows = (open, close) => {
  const toHour = (value) => {
    const match = /^(\d{2}):(\d{2})$/.exec(String(value || ""));
    return match ? Number(match[1]) : null;
  };
  const from = toHour(open);
  const to = toHour(close);
  if (from === null || to === null || to <= from) return [];
  const rows = [];
  for (let hour = from; hour < to; hour += 1) rows.push(pad(hour) + ":00");
  return rows;
};

export default function DayScheduleGrid({
  locations = [],
  allowLocationPicker = false,
  initialLocationId = null,
  title = "Today's appointments",
  style,
}) {
  const usableLocations = useMemo(
    () => (locations || []).filter((location) => location && location._id),
    [locations],
  );

  const [locationId, setLocationId] = useState(
    initialLocationId || usableLocations[0]?._id || null,
  );
  /**
   * EMPTY MEANS "TODAY, AS THE SHOP SEES IT" (2026-10-23).
   *
   * The browser used to compute the date, and a clock that is a day out (or a browser in another timezone)
   * silently asked for the wrong day — which is exactly what an empty grid looks like when the screen is fine.
   * With no date the server answers for the shop's own timezone and returns the date it used, and the header
   * renders THAT, so what is drawn and what is labelled cannot disagree.
   */
  const [date, setDate] = useState("");
  const [state, setState] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [schedule, setSchedule] = useState(null);

  // A dashboard that loads its locations after this renders must not be left with a null location.
  useEffect(() => {
    if (locationId && usableLocations.some((l) => l._id === locationId)) return;
    if (usableLocations.length > 0) setLocationId(usableLocations[0]._id);
  }, [usableLocations, locationId]);

  const load = useCallback(async () => {
    if (!locationId) return;
    setState("loading");
    setError("");
    try {
      const token = localStorage.getItem("ogestyle_token");
      const query = date ? `?date=${date}` : "";
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/shops/${locationId}/day-schedule${query}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(payload.message || "Could not load the schedule.");
        setState("error");
        return;
      }
      setSchedule(payload);
      setState("ready");
    } catch {
      setError("Could not reach the server.");
      setState("error");
    }
  }, [locationId, date]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = schedule ? hourRows(schedule.hours?.open, schedule.hours?.close) : [];

  /**
   * AN APPOINTMENT OCCUPIES THE PART OF THE AXIS IT ACTUALLY USES (user, 2026-10-23: *"if its possible that the
   * appointment takes the portion that it owns in the time axis, we would not need to layer appointments on one
   * another"*).
   *
   * The grid used to bucket a booking into the hour it STARTED, so 09:45-10:15 sat entirely inside the 9 AM cell
   * and 09:45-10:15 next to a 09:00-09:45 stacked on top of it. That misreports the diary twice: the 10 AM column
   * looks free while somebody is in the chair, and two short appointments look like a clash.
   *
   * So the track is measured in MINUTES, not cells, and every block is placed by percentage:
   *   left  = (start - open) / (close - open)
   *   width = (end - start)   / (close - open)
   * A booking that straddles an hour boundary now straddles it on screen, and two bookings that do not overlap in
   * time cannot overlap visually.
   */
  const minutesOf = (hhmm, fallback) => {
    const match = /^(\d{2}):(\d{2})$/.exec(String(hhmm || ""));
    if (!match) return fallback;
    return Number(match[1]) * 60 + Number(match[2]);
  };

  const openMinutes = minutesOf(schedule?.hours?.open, 0);
  const closeMinutes = minutesOf(
    schedule?.hours?.close,
    openMinutes + Math.max(rows.length, 1) * 60,
  );
  const axisMinutes = Math.max(closeMinutes - openMinutes, 60);

  const clampPercent = (value) => Math.max(0, Math.min(100, value));
  const leftPercent = (booking) =>
    clampPercent(((minutesOf(booking.start, openMinutes) - openMinutes) / axisMinutes) * 100);
  const widthPercent = (booking) => {
    const start = minutesOf(booking.start, openMinutes);
    const end = minutesOf(booking.end, start + 15);
    // A zero-width block is invisible; a minimum keeps a 5-minute appointment clickable and readable.
    return Math.max(1.2, clampPercent(((end - start) / axisMinutes) * 100));
  };

  /** Bookings per professional, flat — the axis decides the position, so there is nothing to bucket by hour. */
  const bookingsByProfessional = useMemo(() => {
    const map = new Map();
    for (const person of schedule?.professionals || []) map.set(person.id, []);
    for (const booking of schedule?.bookings || []) {
      if (!booking.staffId || !map.has(booking.staffId)) continue;
      map.get(booking.staffId).push(booking);
    }
    return map;
  }, [schedule]);

  /**
   * COLOUR PER PROFESSIONAL (user, 2026-10-23). A colour that belongs to a person, not to a row position, so the
   * same professional keeps the same colour when the roster order changes.
   */
  const PROFESSIONAL_COLOURS = [
    { bg: "#eff6ff", border: "#2d6da3", text: "#1e3a8a" },
    { bg: "#ecfdf5", border: "#059669", text: "#065f46" },
    { bg: "#fef3c7", border: "#d97706", text: "#92400e" },
    { bg: "#fce7f3", border: "#db2777", text: "#9d174d" },
    { bg: "#ede9fe", border: "#7c3aed", text: "#5b21b6" },
    { bg: "#e0f2fe", border: "#0284c7", text: "#075985" },
  ];
  const colourFor = (index) => PROFESSIONAL_COLOURS[index % PROFESSIONAL_COLOURS.length];

  const dayLabel = new Date(`${schedule?.date || date || todayInLocalTime()}T12:00:00Z`).toLocaleDateString(
    undefined,
    { weekday: "long", day: "numeric", month: "long" },
  );
  /** The date the arrows step from: the server's answer once loaded, otherwise local today. */
  const anchorDate = schedule?.date || date || todayInLocalTime();

  return (
    <section
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "24px",
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#111827" }}>
          {title}
        </h3>
        <span style={{ fontSize: "13px", color: "#6b7280" }}>{dayLabel}</span>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
          {/* THE LOCATION PICKER IS R1's (user, 2026-10-23): R1 oversees every location, R2 is already
              standing in one. Shown only when asked for AND when there is more than one place to choose. */}
          {allowLocationPicker && usableLocations.length > 1 && (
            <select
              value={locationId || ""}
              onChange={(e) => setLocationId(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "13px",
              }}
            >
              {usableLocations.map((location) => (
                <option key={location._id} value={location._id}>
                  {location.name || "Location"}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={() => setDate(shiftDate(anchorDate, -1))}
            style={arrowStyle}
            aria-label="Previous day"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setDate("")}
            style={{ ...arrowStyle, width: "auto", padding: "0 10px", fontSize: "12px" }}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setDate(shiftDate(anchorDate, 1))}
            style={arrowStyle}
            aria-label="Next day"
          >
            ›
          </button>
        </div>
      </div>

      {state === "loading" && (
        <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>Loading the day…</p>
      )}

      {state === "error" && (
        <p style={{ margin: 0, fontSize: "13px", color: "#b91c1c" }}>{error}</p>
      )}

      {state === "ready" && schedule && (
        <>
          {/* A DAY WITH NO CONFIRMED APPOINTMENTS SAYS SO. Only confirmed bookings are drawn (user, 2026-10-23),
              which means holds, pending requests and cancellations are all absent by design — an empty grid has to
              distinguish "nothing booked" from "the screen failed". */}
          {schedule.bookings.length === 0 && (
            <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#6b7280" }}>
              No confirmed appointments on {schedule.weekday}. Unpaid holds, requests and
              cancellations are not shown.
            </p>
          )}
          {schedule.hours?.state === "unset" ? (
            /* NOT CLOSED — UNKNOWN (reported 2026-10-23: *"Because there are no bookings doesn't mean the shop is
               closed"*). The previous message asserted a closure whenever the hours lookup came back empty, which
               happened for every shop because the field being read was the wrong one. An unset weekday is a gap
               in the record, and saying so is the only honest answer: the shop may well be open. */
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              Opening hours have not been set for {schedule.weekday}, so there is no grid to
              draw. This is not the same as being closed — set them in the location's business
              hours.
            </p>
          ) : !schedule.hours?.active ? (
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              The shop is closed on {schedule.weekday} — its business hours mark the day as
              not open.
            </p>
          ) : rows.length === 0 ? (
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              The opening hours for {schedule.weekday} could not be read, so there is no grid to
              draw.
            </p>
          ) : schedule.professionals.length === 0 ? (
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              No professionals are assigned to this location yet, so there is nobody to
              schedule.
            </p>
          ) : (
            <div>
              {/* NO AXIS LABELS (user, 2026-10-23: *"remove the time and professionals label"*). "Professionals ↓"
                  and "Time →" restated what the header row and the name column already show, and cost a line of
                  the card to do it. The GRID ITSELF still has to fit without horizontal scroll (same message:
                  *"I don't want the visible horizontal scroll"*): every hour column is an equal share of the
                  available width, so a narrow screen gets thinner hours, never a scrollbar. */}
              <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb" }}>
                <div
                  style={{
                    flex: `0 0 ${PROFESSIONAL_COLUMN_WIDTH}`,
                    width: PROFESSIONAL_COLUMN_WIDTH,
                    fontSize: "9px",
                    textTransform: "uppercase",
                    color: "#6b7280",
                    padding: "3px 4px",
                  }}
                >
                  Professional
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "grid",
                    gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
                  }}
                >
                  {rows.map((hour) => (
                    <div
                      key={hour}
                      style={{
                        fontSize: "9px",
                        fontWeight: 600,
                        color: "#374151",
                        padding: "3px 2px",
                        borderLeft: "1px solid #f3f4f6",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "clip",
                      }}
                    >
                      {label12h(hour)}
                    </div>
                  ))}
                </div>
              </div>

              {schedule.professionals.map((person, index) => {
                const colour = colourFor(index);
                const personBookings = bookingsByProfessional.get(person.id) || [];
                return (
                  <div
                    key={person.id}
                    style={{ display: "flex", borderBottom: "1px solid #f3f4f6" }}
                  >
                    <div
                      title={person.name}
                      style={{
                        flex: `0 0 ${PROFESSIONAL_COLUMN_WIDTH}`,
                        width: PROFESSIONAL_COLUMN_WIDTH,
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "4px 4px",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#111827",
                        overflow: "hidden",
                      }}
                    >
                      <span
                        style={{
                          flex: "0 0 auto",
                          width: "7px",
                          height: "7px",
                          borderRadius: "99px",
                          background: colour.border,
                        }}
                      />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {/* FIRST NAME ALONE (user, 2026-10-23): a row header has one line to say who it is. */}
                        {String(person.name || "").split(" ")[0] || "Professional"}
                      </span>
                    </div>

                    {/* THE TRACK IS MEASURED IN MINUTES. Hour cells are only the background grid; the blocks are
                        placed by percentage of the day, so an appointment that crosses an hour boundary crosses it
                        on screen and two bookings that do not overlap in time cannot overlap visually. */}
                    <div style={{ position: "relative", flex: 1, height: "46px" }}>
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "grid",
                          gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
                        }}
                      >
                        {rows.map((hour) => (
                          <div key={hour} style={{ borderLeft: "1px solid #f3f4f6" }} />
                        ))}
                      </div>

                      {personBookings.map((booking) => (
                        /* THE TOOLTIP KEEPS THE FULL STORY (user, 2026-10-23: *"I like the tooltip you did as
                           well"*), including the end time the block no longer prints: a hover is where a detail
                           belongs once the block has made room for the three things read at a glance. */
                        <div
                          key={booking.id}
                          title={`${label12h(booking.start)}–${label12h(booking.end)} · ${booking.service}${
                            booking.customer ? ` · ${booking.customer}` : ""
                          }`}
                          style={{
                            position: "absolute",
                            top: "2px",
                            bottom: "2px",
                            left: `${leftPercent(booking)}%`,
                            width: `${widthPercent(booking)}%`,
                            boxSizing: "border-box",
                            background: colour.bg,
                            border: `1px solid ${colour.border}`,
                            borderLeft: `3px solid ${colour.border}`,
                            borderRadius: "3px",
                            padding: "2px 3px",
                            overflow: "hidden",
                            fontSize: "9px",
                            lineHeight: 1.2,
                            color: colour.text,
                          }}
                        >
                          {/* THREE LINES, ONE FACT EACH (user, 2026-10-23): *"we don't need to show appointment
                              end time, start time is enough … 3 info to display, start time, first name of
                              customer and service name, each on its row"*. The end time is a duration fact, and
                              a block wide enough for a start time is not wide enough to also carry a range. */}
                          <div
                            style={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {label12h(booking.start)}
                          </div>
                          {firstNameOf(booking.customer) && (
                            <div
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {firstNameOf(booking.customer)}
                            </div>
                          )}
                          <div
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {booking.service}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}

const arrowStyle = {
  width: "26px",
  height: "26px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  background: "white",
  color: "#374151",
  fontSize: "14px",
  lineHeight: 1,
  cursor: "pointer",
};
